/**
 * Notification Service
 * 
 * Provides lightweight DOM-based toast notifications for user feedback.
 * Non-blocking, auto-dismissing notifications that don't obstruct board interaction.
 * 
 * @module notification.service
 */

import { Injectable } from '@angular/core';

/**
 * Toast notification type for styling
 */
export type NotificationType = 'error' | 'success' | 'info' | 'warning';

/**
 * Configuration for individual toast notifications
 */
export interface ToastConfig {
  /** Message to display */
  message: string;
  /** Type of notification (affects styling) */
  type: NotificationType;
  /** Duration in milliseconds before auto-dismiss (0 = manual dismiss only) */
  duration: number;
}

/**
 * Internal tracking of active toasts
 */
interface ActiveToast {
  id: string;
  element: HTMLElement;
  timeoutId: number | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private toastContainer: HTMLElement | null = null;
  private activeToasts: Map<string, ActiveToast> = new Map();
  private toastIdCounter = 0;

  /**
   * Get or create the toast container element
   */
  private getContainer(): HTMLElement {
    if (this.toastContainer && document.body.contains(this.toastContainer)) {
      return this.toastContainer;
    }

    // Check if container already exists (in case of multiple service instances)
    let container = document.getElementById('notification-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'notification-toast-container';
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'true');
      this.applyContainerStyles(container);
      document.body.appendChild(container);
    }
    this.toastContainer = container;
    return container;
  }

  /**
   * Apply CSS styles to the toast container
   */
  private applyContainerStyles(container: HTMLElement): void {
    Object.assign(container.style, {
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '10000',
      display: 'flex',
      flexDirection: 'column-reverse',
      alignItems: 'center',
      gap: '10px',
      pointerEvents: 'none',
    });
  }

  /**
   * Get styles based on notification type
   */
  private getTypeStyles(type: NotificationType): Partial<CSSStyleDeclaration> {
    const baseStyles: Partial<CSSStyleDeclaration> = {
      padding: '12px 20px',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '14px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      maxWidth: '400px',
      textAlign: 'center',
      pointerEvents: 'auto',
      cursor: 'pointer',
      opacity: '0',
      transition: 'opacity 0.3s ease-in-out',
    };

    const typeColors: Record<NotificationType, string> = {
      error: '#d32f2f',
      success: '#388e3c',
      info: '#1976d2',
      warning: '#f57c00',
    };

    return {
      ...baseStyles,
      backgroundColor: typeColors[type],
    };
  }

  /**
   * Create and display a toast notification
   */
  private createToast(config: ToastConfig): string {
    const container = this.getContainer();
    const toastId = `toast-${++this.toastIdCounter}`;

    const toast = document.createElement('div');
    toast.id = toastId;
    toast.textContent = config.message;
    toast.setAttribute('role', 'alert');
    
    // Apply styles
    const styles = this.getTypeStyles(config.type);
    Object.assign(toast.style, styles);

    // Add click to dismiss
    toast.addEventListener('click', () => this.dismiss(toastId));

    container.appendChild(toast);

    // Trigger fade-in animation
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });

    // Set up auto-dismiss
    let timeoutId: number | null = null;
    if (config.duration > 0) {
      timeoutId = window.setTimeout(() => {
        this.dismiss(toastId);
      }, config.duration);
    }

    this.activeToasts.set(toastId, {
      id: toastId,
      element: toast,
      timeoutId,
    });

    return toastId;
  }

  /**
   * Dismiss a toast by ID
   */
  dismiss(toastId: string): void {
    const toast = this.activeToasts.get(toastId);
    if (!toast) return;

    // Clear timeout if exists
    if (toast.timeoutId) {
      window.clearTimeout(toast.timeoutId);
    }

    // Fade out animation
    toast.element.style.opacity = '0';
    
    // Remove after animation
    setTimeout(() => {
      if (toast.element.parentNode) {
        toast.element.parentNode.removeChild(toast.element);
      }
      this.activeToasts.delete(toastId);
    }, 300);
  }

  /**
   * Dismiss all active toasts
   */
  dismissAll(): void {
    this.activeToasts.forEach((_, toastId) => {
      this.dismiss(toastId);
    });
  }

  /**
   * Show an error notification
   * 
   * @param message Error message to display
   * @param duration Duration in milliseconds (default: 5000)
   * @returns Toast ID for manual dismissal
   */
  showError(message: string, duration = 5000): string {
    return this.createToast({
      message,
      type: 'error',
      duration,
    });
  }

  /**
   * Show a success notification
   * 
   * @param message Success message to display
   * @param duration Duration in milliseconds (default: 3000)
   * @returns Toast ID for manual dismissal
   */
  showSuccess(message: string, duration = 3000): string {
    return this.createToast({
      message,
      type: 'success',
      duration,
    });
  }

  /**
   * Show an info notification
   * 
   * @param message Info message to display
   * @param duration Duration in milliseconds (default: 4000)
   * @returns Toast ID for manual dismissal
   */
  showInfo(message: string, duration = 4000): string {
    return this.createToast({
      message,
      type: 'info',
      duration,
    });
  }

  /**
   * Show a warning notification
   * 
   * @param message Warning message to display
   * @param duration Duration in milliseconds (default: 4000)
   * @returns Toast ID for manual dismissal
   */
  showWarning(message: string, duration = 4000): string {
    return this.createToast({
      message,
      type: 'warning',
      duration,
    });
  }
}
